import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { useNavigate, useParams } from 'react-router-dom'
import Select from 'react-select'
import { useDispatch } from 'react-redux'
import { KTIcon } from '../../../../../../_metronic/helpers'
import { AppDispatch } from '../../../../../redux/store'
import {
  createCouponApi,
  getCouponByIdApi,
  updateCouponApi,
} from '../../../../../redux/slices/CouponSlice'
import { toast } from 'react-toastify'
import { couponTypeOptions, couponValueOptions } from '../../../../../data/crm-data'
import { getProductListApi } from '../../../../../redux/slices/ProductSlice'
import { right } from '@popperjs/core'
import { getDownloadCouponById } from '../../../../../services/CouponService'
import * as Yup from 'yup'

const CouponForm = () => {
  const { id } = useParams() // Get the ID from the route params
  const dispatch = useDispatch<AppDispatch>()
  const [loading, setLoading] = useState(false)
  const [imagePreview, setImagePreview] = useState(null)
  const [productOptions, setProductOptions] = useState<any[]>([])
  const navigate = useNavigate()

  const couponTypeOptions = [
    { label: 'Points', value: 'points' },
    { label: 'Money', value: 'money' },
    { label: 'Both', value: 'both' },
  ];

  // Formik configuration
  const formik = useFormik({
    initialValues: {
      name: '',
      description: '', // <-- ADDED
      start_date: '',
      end_date: '',
      use_limit: '',
      coupon_type: '',
      coupon_money: null,
      coupon_points: null,
      product_id: null,
      is_active: true,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      description: Yup.string().max(250, 'Description cannot exceed 250 characters'), // <-- ADDED
      start_date: Yup.date().required('Start date is required'),
      end_date: Yup.date()
        .required('End date is required')
        .min(Yup.ref('start_date'), 'End date must be after start date'),
      use_limit: Yup.number()
        .required('Use limit is required')
        .min(1, 'Use limit must be at least 1')
        .max(5000, 'Use limit cannot exceed 5000'),
      coupon_type: Yup.string().required('Coupon type is required'),
      coupon_money: Yup.number()
        .when('coupon_type', {
          is: (val: string) => val === 'money' || val === 'both',
          then: (schema) => schema.required('Coupon money is required').min(1, 'Coupon money must be at least 1'),
          otherwise: (schema) => schema.notRequired(),
        }),
      coupon_points: Yup.number()
        .when('coupon_type', {
          is: (val: string) => val === 'points' || val === 'both',
          then: (schema) => schema.required('Coupon points is required').min(1, 'Coupon points must be at least 1'),
          otherwise: (schema) => schema.notRequired(),
        }),
      product_id: Yup.mixed().nullable(),
    }),
    onSubmit: async (values) => {
      // Prepare the payload with conditional fields
      const payload: any = {
        name: values.name,
        description: values.description,
        start_date: values.start_date,
        end_date: values.end_date,
        use_limit: values.use_limit,
        coupon_type: values.coupon_type,
        product_id: values.product_id,
        is_active: values.is_active,
      }

      // Only include coupon_money if coupon type is 'money' or 'both'
      if (values.coupon_type === 'money' || values.coupon_type === 'both') {
        payload.coupon_money = values.coupon_money
      }

      // Only include coupon_points if coupon type is 'points' or 'both'
      if (values.coupon_type === 'points' || values.coupon_type === 'both') {
        payload.coupon_points = values.coupon_points
      }

      // Debug: Log the payload being sent
      console.log('Payload being sent:', payload)
      console.log('Coupon type:', values.coupon_type)

      let data
      if (id) {
        // Update coupon API call
        data = await dispatch(updateCouponApi({ id, payload }))
      } else {
        // Create new coupon API call
        data = await dispatch(createCouponApi(payload))
      }

      if (data.payload.success) {
        setLoading(false)
        toast.success(data.payload.reason)
      } else {
        setLoading(false)
        toast.error(data.payload.reason ? data.payload.reason : 'Something went wrong')
      }

      navigate(-1)
    },
  })

  useEffect(() => {
    dispatch(getProductListApi({ pageNumber: 1, pageSize: 1000, filterkey: '' })).then((res: any) => {
      if (res.payload?.data?.data) {
        const options = res.payload.data.data.map((product: any) => ({
          label: product.name,
          value: product.id,
        }))
        setProductOptions(options)
      }
    })
  }, [])

  useEffect(() => {
    if (id) {
      fetchCouponData()
    }
  }, [id])

  const fetchCouponData = async () => {
    dispatch(getCouponByIdApi(id))
      .then((res) => {
        const couponData = res.payload
        formik.setValues({
          ...couponData,
          description: couponData.description || '', // <-- ADDED
          product_id: couponData.product_id || null,
          coupon_qr_codes_url: couponData.coupon_qr_codes_url || '',
        })
        if (couponData.coupon_qr_codes_url) {
          setImagePreview(couponData.coupon_qr_codes_url)
        } else {
          setImagePreview(null)
        }
      })
      .catch(() => {
        toast.error('An error occurred while fetching coupon data')
      })
  }

  // const handleDownload = () => {
  //  ... (commented code)
  // };

  return (
    <div className='container mt-4'>
      <div className='card shadow' style={{ height: 'auto', minHeight: '500px' }}>
        <form onSubmit={formik.handleSubmit}>
          {/* Card Header */}
          <div className='card-header bg-light d-flex justify-content-between align-items-center position-relative py-6'>
            <h2 className='mb-0'>{id ? 'Edit Coupon' : 'Add Coupon'}</h2>
            <button type='submit' className='btn btn-primary'>
              {id ? 'Update' : 'Submit'}
            </button>

            <span
              className={`position-absolute border-primary`}
              style={{
                top: '12px',
                left: '0',
                height: '70%',
                width: '4px',
                backgroundColor: `var(--bs-primary)`, // Ensures the border color matches the theme
              }}
            ></span>
          </div>

          {/* Card Body */}
          <div className='card-body p-6'>
            <div>
              <div className='row g-3'>
                {/* Name */}
                <div className='col-md-6'>
                  <label htmlFor='name' className='form-label'>
                    Name <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='text'
                    id='name'
                    name='name'
                    className='form-control'
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder='Enter coupon name'
                    disabled={!!id}
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className='text-danger'>{formik.errors.name}</div>
                  )}
                </div>

                {/* Product Dropdown */}
                <div className='col-md-6'>
                  <label className='form-label'>Product</label>
                  <Select
                    options={productOptions}
                    value={productOptions.find((opt) => opt.value === formik.values.product_id)}
                    onChange={(option: any) => formik.setFieldValue('product_id', option?.value || null)}
                    placeholder='Select Product'
                    isClearable
                    isDisabled={!!id}
                  />
                </div>

                {/* Coupon Type */}
                {/* <div className='col-md-6'>
                  <label className='form-label'>
                    Coupon Type <span className='text-danger'>*</span>
                  </label>
                  <Select
                    options={couponTypeOptions}
                    value={couponTypeOptions.find(
                      (option) => option.value === formik.values.coupon_type
                    )}
                    onChange={(selectedOption: any) =>
                      formik.setFieldValue('coupon_type', selectedOption?.value)
                    }
                    onBlur={() => formik.setFieldTouched('coupon_type', true)}
                    placeholder='Select coupon type'
                    disabled={!!id}
                  />
                  {formik.touched.coupon_type && formik.errors.coupon_type && (
                    <div className='text-danger'>{formik.errors.coupon_type}</div>
                  )}
                </div> */}

                <div className='col-md-6'>
                  <label className='form-label'>
                    Coupon Type <span className='text-danger'>*</span>
                  </label>
                  <Select
                    options={couponTypeOptions}
                    value={couponTypeOptions.find(
                      (option) => option.value === formik.values.coupon_type
                    )}
                    onChange={(selectedOption: any) => {
                      formik.setFieldValue('coupon_type', selectedOption?.value)
                      // Clear the fields when coupon type changes to avoid validation issues
                      formik.setFieldValue('coupon_money', null)
                      formik.setFieldValue('coupon_points', null)
                    }}
                    onBlur={() => formik.setFieldTouched('coupon_type', true)}
                    placeholder='Select coupon type'
                    disabled={!!id}
                  />
                  {formik.touched.coupon_type && formik.errors.coupon_type && (
                    <div className='text-danger'>{formik.errors.coupon_type}</div>
                  )}
                </div>

                {/* Coupon Money - Show only when coupon type is 'money' or 'both' */}
                {(formik.values.coupon_type === 'money' || formik.values.coupon_type === 'both') && (
                  <div className='col-md-6'>
                    <label className='form-label'>
                      Coupon Money <span className='text-danger'>*</span>
                    </label>
                    <input
                      type='number'
                      id='coupon_money'
                      name='coupon_money'
                      className='form-control'
                      value={formik.values.coupon_money || ''}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder='Enter Coupon Money'
                      disabled={!!id}
                    />
                    {formik.touched.coupon_money && formik.errors.coupon_money && (
                      <div className='text-danger'>{formik.errors.coupon_money}</div>
                    )}
                  </div>
                )}

                {/* Coupon Points - Show only when coupon type is 'points' or 'both' */}
                {(formik.values.coupon_type === 'points' || formik.values.coupon_type === 'both') && (
                  <div className='col-md-6'>
                    <label className='form-label'>
                      Coupon Points <span className='text-danger'>*</span>
                    </label>
                    <input
                      type='number'
                      id='coupon_points'
                      name='coupon_points'
                      className='form-control'
                      value={formik.values.coupon_points || ''}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder='Enter coupon points'
                      disabled={!!id}
                    />
                    {formik.touched.coupon_points && formik.errors.coupon_points && (
                      <div className='text-danger'>{formik.errors.coupon_points}</div>
                    )}
                  </div>
                )}

                {/* Use Limit */}
                <div className='col-md-6'>
                  <label htmlFor='use_limit' className='form-label'>
                    Use Limit <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='number'
                    id='use_limit'
                    name='use_limit'
                    className='form-control'
                    value={formik.values.use_limit}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10)
                      if (value <= 5000) {
                        formik.setFieldValue('use_limit', value)
                      }
                    }}
                    onBlur={formik.handleBlur}
                    placeholder='Enter maximum usage limit'
                    disabled={!!id}
                    max={1000} // HTML attribute to enforce a limit on the UI
                  />
                  {formik.touched.use_limit && formik.errors.use_limit && (
                    <div className='text-danger'>{formik.errors.use_limit}</div>
                  )}
                </div>

                {/* Start Date */}
                <div className='col-md-6'>
                  <label htmlFor='start_date' className='form-label'>
                    Start Date <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='date'
                    id='start_date'
                    name='start_date'
                    className='form-control'
                    value={formik.values.start_date}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!!id}
                  />
                  {formik.touched.start_date && formik.errors.start_date && (
                    <div className='text-danger'>{formik.errors.start_date}</div>
                  )}
                </div>

                {/* End Date */}
                <div className='col-md-6'>
                  <label htmlFor='end_date' className='form-label'>
                    End Date <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='date'
                    id='end_date'
                    name='end_date'
                    className='form-control'
                    value={formik.values.end_date}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.end_date && formik.errors.end_date && (
                    <div className='text-danger'>{formik.errors.end_date}</div>
                  )}
                </div>

                {/* Description */}
                <div className='col-md-12'>
                  <label htmlFor='description' className='form-label'>
                    Description
                  </label>
                  <textarea
                    id='description'
                    name='description'
                    className='form-control'
                    rows={3}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder='Enter a short description for the coupon'
                  // disabled={!!id}
                  />
                  {formik.touched.description && formik.errors.description && (
                    <div className='text-danger'>{formik.errors.description}</div>
                  )}
                </div>

                {/* Is Active */}
                <div className='col-md-12 pt-6 d-flex align-items-center'>
                  <input
                    type='checkbox'
                    id='is_active'
                    name='is_active'
                    className='form-check-input mx-4'
                    checked={formik.values.is_active}
                    onChange={(e) => formik.setFieldValue('is_active', e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <label
                    htmlFor='is_active'
                    className='form-label me-3 p-0 m-0'
                    style={{ cursor: 'pointer' }}
                  >
                    Is Active
                  </label>
                </div>

                {/* QR Code Display (commented) */}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CouponForm
